import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Info, ShoppingCart, Menu, X } from 'lucide-react'
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../core/hooks/useTranslation'
import { useBusinessColor } from '../core/hooks/useBusinessColor'
import { useCartStore } from '../core/stores/cartStore'
import { useMenuStore } from '../core/stores/menuStore'
import Footer from '../components/layout/Footer'
import CartModal from '../components/menu/CartModal'

interface MenuLayoutProps {
  children: ReactNode
  slug: string
}

export default function MenuLayout({ children, slug }: MenuLayoutProps) {
  const { isRTL } = useTranslation()
  const location = useLocation()
  const { toggleCart, getTotalItems } = useCartStore()
  const { menu } = useMenuStore()
  const { color, darker, opacity15 } = useBusinessColor()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const businessName = menu?.business?.name || (isRTL ? 'قائمة المطعم' : 'Restaurant Menu')
  const businessLogo = menu?.business?.logoUrl
  const businessAddress = menu?.business?.address
  const businessColor = menu?.business?.color || '#1E293B'

  const menuItems = [
    {
      path: `/${slug}`,
      label: isRTL ? 'المنيو' : 'Menu',
      labelMobile: isRTL ? 'المنيو' : 'Menu',
      icon: ClipboardDocumentListIcon
    },
    {
      path: `/${slug}/about`,
      label: isRTL ? 'من نحن' : 'About',
      icon: Info
    }
  ]

  const isActive = (path: string) => {
    // Normalize paths to handle trailing slashes
    const normalizedPathname = location.pathname.replace(/\/$/, '')
    const normalizedPath = path.replace(/\/$/, '')
    // Exact match only - don't use startsWith to avoid matching parent paths
    return normalizedPathname === normalizedPath
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-stone-100 flex flex-col">
      {/* Navigation Header */}
      <nav className="border-b border-gray-100 bg-white/95 backdrop-blur-sm sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            {/* Logo and Navigation */}
            <div className="flex items-center gap-4 sm:gap-6 flex-1">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(true)}
                className="md:hidden p-2 rounded-lg transition-colors flex items-center justify-center"
                style={{
                  backgroundColor: `${businessColor}15`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${businessColor}25`
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${businessColor}15`
                }}
              >
                <Menu className="w-5 h-5" style={{ color: businessColor }} />
              </button>

              {/* Business Logo and Name */}
              <Link to={`/${slug}`} className="flex items-center space-x-2 rtl:space-x-reverse group flex-shrink-0">
                {businessLogo ? (
                  <div className="w-7 h-7 sm:w-10 sm:h-10 rounded-lg border border-gray-200 bg-white flex items-center justify-center shadow-sm overflow-hidden flex-shrink-0">
                    <img
                      src={businessLogo}
                      alt={`${businessName} logo`}
                      className="w-5 h-5 sm:w-8 sm:h-8 object-contain transition-transform group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div 
                    className="w-7 h-7 sm:w-10 sm:h-10 rounded-lg border flex items-center justify-center shadow-sm flex-shrink-0"
                    style={{
                      borderColor: `${businessColor}40`,
                      backgroundColor: `${businessColor}15`,
                    }}
                  >
                    <ClipboardDocumentListIcon className="w-4 h-4 sm:w-6 sm:h-6" style={{ color: businessColor }} />
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="text-xs sm:text-base font-bold text-[#1E293B] truncate max-w-[120px] sm:max-w-none">
                    {businessName}
                  </span>
                  {businessAddress && (
                    <p className="text-[10px] sm:text-xs text-gray-500 leading-tight truncate max-w-[120px] sm:max-w-none hidden sm:block">
                      {businessAddress}
                    </p>
                  )}
                </div>
              </Link>

              {/* Navigation Menu - Desktop */}
              <nav className="hidden md:flex items-center gap-1 overflow-x-auto scrollbar-hide">
                {menuItems.map((item) => {
                  const Icon = item.icon
                  const active = isActive(item.path)
                  return (
                    <Link
                      key={`${item.path}-${location.pathname}`}
                      to={item.path}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                        active
                          ? 'text-white shadow-sm'
                          : 'bg-white/80 text-stone-700 border border-stone-200 hover:border-transparent'
                      }`}
                      style={active ? {
                        background: `linear-gradient(to right, ${color}, ${darker})`,
                        borderColor: 'transparent',
                      } : {
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        color: '#292524',
                        borderColor: '#e7e5e4',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          const target = e.currentTarget
                          target.style.backgroundColor = opacity15
                          target.style.color = color
                          target.style.borderColor = 'transparent'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          const target = e.currentTarget
                          target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'
                          target.style.color = '#292524'
                          target.style.borderColor = '#e7e5e4'
                        }
                      }}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </Link>
                  )
                })}
              </nav>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              {/* <LanguageToggle /> */}
              
              {/* Cart Button */}
              <button
                onClick={toggleCart}
                className="relative p-2 rounded-lg transition-colors flex items-center justify-center"
                style={{
                  backgroundColor: `${businessColor}15`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${businessColor}25`
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${businessColor}15`
                }}
              >
                <ShoppingCart className="w-5 h-5" style={{ color: businessColor }} />
                {getTotalItems(slug) > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 w-5 h-5 text-white rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ backgroundColor: businessColor }}
                  >
                    {getTotalItems(slug) > 99 ? '99+' : getTotalItems(slug)}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Sidebar */}
      {isMenuOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-[60] md:hidden transition-opacity"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Sidebar */}
          <nav
            className={`fixed top-0 ${isRTL ? 'right-0' : 'left-0'} w-64 h-full bg-white shadow-2xl z-[70] md:hidden transform transition-transform duration-300 flex flex-col`}
          >
            {/* Header */}
            <div className="border-b border-gray-100 bg-white/95 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
              <div className="px-4 sm:px-6">
                <div className="flex justify-between items-center py-3">
                  <div className="flex items-center gap-3">
                    {/* <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                      <ClipboardDocumentListIcon className="w-5 h-5 text-primary-600" />
                    </div> */}
                    <div>
                      <h2 className="text-sm font-bold text-[#1E293B]">
                        {businessName}
                      </h2>
                      <p className="text-xs text-gray-500 leading-tight">
                        {isRTL ? 'القائمة الرئيسية' : 'Main Menu'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
              </div>
            </div>

            {/* Navigation Menu */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon
                  const active = isActive(item.path)
                  const displayLabel = (item as any).labelMobile || item.label
                  
                  return (
                    <Link
                      key={`${item.path}-${location.pathname}`}
                      to={item.path}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        active
                          ? 'text-white shadow-sm'
                          : 'bg-white/80 text-stone-700 border border-stone-200 hover:border-transparent'
                      }`}
                      style={active ? {
                        background: `linear-gradient(to right, ${color}, ${darker})`,
                        borderColor: 'transparent',
                      } : {
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        color: '#292524',
                        borderColor: '#e7e5e4',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          const target = e.currentTarget
                          target.style.backgroundColor = opacity15
                          target.style.color = color
                          target.style.borderColor = 'transparent'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          const target = e.currentTarget
                          target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'
                          target.style.color = '#292524'
                          target.style.borderColor = '#e7e5e4'
                        }
                      }}
                      onClick={() => {
                        setIsMenuOpen(false)
                      }}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
                        {displayLabel}
                      </span>
                    </Link>
                  )
                })}
              </div>
            </div>
          </nav>
        </>
      )}

      {/* Cart Modal */}
      <CartModal />

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <Footer variant="menu" />
    </div>
  )
}

